import { useState, useCallback, useRef } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { svc_getAccountsGroupedByType, svc_getDebtAccounts } from "../services/account.service";
import { useResponseMessages } from "./useResponseMessage";
import { isDebt } from '../constants/ACCOUNT_TYPE';
import {
    getDefaultPaymentType,
    getMinFixedAmount,
    getDefaultPaymentAmount,
} from '../utilities/snowball-utilities';

/**
 * Custom hook for managing snowball account state and operations
 * Separates account management logic from the main component
 */
export const useSnowballAccounts = () => {
    const { getAccessTokenSilently, user } = useAuth0();
    const { addMessage } = useResponseMessages();
    
    const [debtAccounts, setDebtAccounts] = useState([]);
    const [nonDebtAccounts, setNonDebtAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Use ref to track if accounts have been fetched to prevent unnecessary refetches
    const hasFetchedAccounts = useRef(false);

    /**
     * Adds default properties to each debt account for snowball calculations
     */
    const addAccountProps = useCallback((account, index) => {
        return {
            ...account,
            index,
            paymentType: getDefaultPaymentType(account),
            minFixedAmount: getMinFixedAmount(account),
            defaultPaymentAmount: getDefaultPaymentAmount(account),
            paymentSchedule: null,
            snowballData: null,
            extraPayments: []
        };
    }, []);

    /**
     * Fetches all accounts and separates debt from non-debt accounts
     */
    const getAccounts = useCallback(async () => {
        if (isLoading) return;
        
        setIsLoading(true);
        
        try {
            const accessToken = await getAccessTokenSilently();
            const [allAccountsResponse, debtAccountsResponse] = await Promise.all([
                svc_getAccountsGroupedByType(accessToken, user.sub),
                svc_getDebtAccounts(accessToken, user.sub)
            ]);

            const { data: allAccountsData } = allAccountsResponse;
            const { data: debtAccountsData } = debtAccountsResponse;

            // Process debt accounts
            const filteredDebtAccounts = debtAccountsData
                .filter(account => account.balance > 0)
                .sort((a, b) => a.balance - b.balance)
                .map(addAccountProps);

            // Process non-debt accounts
            const filteredNonDebtAccounts = allAccountsData
                .filter(group => !isDebt(group.type) && group.accounts.length > 0)
                .reduce((acc, group) => acc.concat(group.accounts), [])
                .sort((a, b) => a.name.localeCompare(b.name));

            setDebtAccounts(filteredDebtAccounts);
            setNonDebtAccounts(filteredNonDebtAccounts);
            hasFetchedAccounts.current = true;

        } catch (error) {
            console.error('Error fetching accounts:', error);
            addMessage({
                error: 'There was an error fetching your accounts.'
            });
        } finally {
            setIsLoading(false);
        }
    }, [getAccessTokenSilently, user.sub, addMessage, addAccountProps, isLoading]);

    /**
     * Refetches accounts with user confirmation if accounts exist
     */
    const handleRefetchAccounts = useCallback(() => {
        const hasExistingAccounts = debtAccounts && debtAccounts.length > 0;
        
        if (hasExistingAccounts) {
            const confirmRefetch = window.confirm(
                "Are you sure you want to re-fetch your accounts? This will clear any adjustments you've made."
            );
            if (!confirmRefetch) return;
        }
        
        hasFetchedAccounts.current = false;
        getAccounts();
    }, [debtAccounts, getAccounts]);

    /**
     * Updates a specific debt account and maintains order
     */
    const updateAccount = useCallback((updatedAccount) => {
        setDebtAccounts(prevAccounts => {
            const updatedAccounts = [...prevAccounts];
            const accountIndex = updatedAccounts.findIndex(acc => acc.id === updatedAccount.id);
            
            if (accountIndex !== -1) {
                updatedAccounts[accountIndex] = updatedAccount;
            }
            
            return updatedAccounts;
        });
    }, []);

    /**
     * Updates the order of debt accounts (for drag and drop)
     */
    const updateAccountOrder = useCallback((reorderedAccounts) => {
        const accountsWithUpdatedIndices = reorderedAccounts.map((account, index) => ({
            ...account,
            index
        }));
        setDebtAccounts(accountsWithUpdatedIndices);
    }, []);

    return {
        // State
        debtAccounts,
        nonDebtAccounts,
        isLoading,
        hasFetchedAccounts: hasFetchedAccounts.current,
        
        // Actions
        getAccounts,
        handleRefetchAccounts,
        updateAccount,
        updateAccountOrder,
        setDebtAccounts // For external calculations that need to update multiple accounts
    };
};